import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, forkJoin, BehaviorSubject } from 'rxjs';
import { PromptWithActionDialogComponent } from '../dialogs/prompt-with-action/prompt-with-action.component';

import { CommonService } from '../services/common.service';
import { ReportsService } from './reports.service';

interface DynamicFormMeta {
  formId: string;
  name: string;
  pageType: string;
  title: string;
  titleTranslationTag?: string;
}

export interface DynamicFormsStruct {
  formId: string;
  metadata: DynamicFormMeta;
  pages: Array<any>;
}

export interface DynamicFormsEvent {
  formData: any;
  submissionId?: string;
  formComplete?: boolean;
  triggeredByAutosave: boolean;
}

export interface DynamicFormsCompleteEvent extends DynamicFormsEvent {
  formValid?: boolean;
  manuallyTriggered?: boolean;
}

// Coach Intervention
export enum TabsEnum {
  insights = 0,
  history,
  roadRisk,
}

export interface PccCrash {
  crashId?: string;
  date?: string;
  description?: string;
}

export interface Detail {
  id: number;
  company: number;
  insight: string;
  manager: number;
}

export interface VrmTrainingResponse {
  data: {
    [key: string]: {
      assigned: number;
      completed: number;
      notStarted: number;
      overdue?: number;
      modules: {
        assigned: string;
        courseCode: string;
        courseName: string;
        datedone: string;
        outcome: string;
        overdue?: boolean;
        due?: string;
        extraData?: { [key: string]: any };
      }[];
    };
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class DrilldownFormsService {
  // Insights
  currentInsightDetail: Detail;

  // Coach Intervention
  selectedDriver: any;
  showFormPane: boolean = false;
  widenForm: boolean = false;
  formVisibility = new BehaviorSubject(false);
  dynamicFormIsVisible = false;
  viewOnlyFormVisibility = new BehaviorSubject(false);
  viewOnlyDynamicFormIsVisible = false;
  selectedDriverPaneOpened: boolean = true;
  launchedIntervention: string;
  launchedInterventionHistory: any = {};
  virtualSafetyDiscussionInterventionValue: string = 'VirtualSafetyDiscussion';
  pccReviewInterventionValue: string = 'PccReview';
  oneToOneInterventionValue: string = 'OneToOne';
  driverImprovementInterventionValue: string = 'DriverImprovementReview';
  driverIndexProCoachInterventionValue: string = 'DriverIndexProCoach';
  interventionsToAutosave: Array<string> = ['PccReview', 'OneToOne', 'DriverIndexProCoach'];
  interventionsToNotSaveOnLaunch: Array<string> = ['OneToOne'];
  collapsibleInterventions: Array<string> = ['PccReview', 'OneToOne'];
  interventionsWithCustomPrintButton: Array<string> = ['PccReview', 'OneToOne'];
  selectedPccReviewCrash: PccCrash;
  currentFormSubmissionId: string = '';
  currentFormStruct: DynamicFormsStruct;
  currentViewOnlyFormStruct: DynamicFormsStruct;
  selectedDynamicFormData: any;
  selectedViewOnlyDynamicFormData: any;
  formIsReadonly: boolean = false;
  drilldownTabs = {
    insights: { id: TabsEnum.insights, nameId: 'insights' },

    history: { id: TabsEnum.history, nameId: 'coaching toolkit' },
    'coaching toolkit': { id: TabsEnum.history, nameId: 'coaching toolkit' },

    roadRisk: { id: TabsEnum.roadRisk, nameId: 'roadrisk' },
  };
  currentTabIndex: number = TabsEnum.insights;
  coachingReasonsDataMapping: any = {};

  constructor(private cService: CommonService, private rptService: ReportsService, private dialog: MatDialog) {
    // TODO: Move somewhere else so it can reused in all the various places.
    this.coachingReasonsDataMapping = {
      'Not Available': this.cService.readyTranslations.reportData.notAvailable,
      ficoLt500Week: this.cService.readyTranslations.reportData.weeklyFicoLessThan500,
      ficoLt559Week: this.cService.readyTranslations.reportData.weeklyFicoLessThan559,
      'FICO Safe Driving Score between 560 and 709 for two consecutive weeks':
        this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
      'fico560-709TwoWeeks': this.cService.readyTranslations.reportData.ficoSafeScoreBetween560And709,
      driverIndexCoachCoachingReason: this.cService.readyTranslations.reportData.driverIndexCoachCoachingReason,
      pccCoachingReason: this.cService.readyTranslations.reportData.pccCoachingReason,
      driverIndexProCoachingReason: this.cService.readyTranslations.reportData.driverIndexProCoachingReason,
    };

    this.formVisibility.subscribe({ next: (isVisible) => (this.dynamicFormIsVisible = isVisible) });
    this.viewOnlyFormVisibility.subscribe({ next: (isVisible) => (this.viewOnlyDynamicFormIsVisible = isVisible) });
  }

  // Coach Intervention Events / Methods
  closeFormPane() {
    this.showFormPane = false;
    this.formVisibility.next(false);
    this.widenForm = false;
    this.launchedIntervention = '';
    this.currentFormSubmissionId = '';
    this.currentFormStruct = null;
    this.selectedDynamicFormData = null;
    this.formIsReadonly = false;
  }

  getImagesWithDetails(imageId: string | number): Observable<any> {
    return this.cService.commonGet(`driverindex/incidents_collisions/images/${imageId}`);
  }

  private shouldCreateInterventionOnLaunch(selectedIntervention: string = ''): boolean {
    return (
      selectedIntervention &&
      this.interventionsToAutosave.includes(selectedIntervention) &&
      !this.interventionsToNotSaveOnLaunch.includes(selectedIntervention)
    );
  }

  launchSelectedIntervention(
    selectedHistory: any,
    selectedIntervention: string = '',
    formHistoryId: string = '',
    pccReviewCrash: PccCrash = {}
  ) {
    // NOTE: This is important as it makes forms hot-swappable
    this.showFormPane = false;

    this.selectedPccReviewCrash = pccReviewCrash;
    this.launchedIntervention = selectedIntervention;

    setTimeout(() => {
      if (selectedHistory) {
        this.launchedIntervention = selectedHistory.interventionValue;
        this.launchedInterventionHistory = selectedHistory;
      } else {
        this.launchedInterventionHistory = null;
      }

      if (this.launchedIntervention) {
        this.formIsReadonly = selectedHistory ? selectedHistory.readOnly : false;
        formHistoryId = selectedHistory ? selectedHistory.submissionId : '';
        const securePin = this.selectedDriver.securePin ?? false;

        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.working);

        this.currentFormSubmissionId = '';
        if (formHistoryId) {
          this.currentFormSubmissionId = formHistoryId;
        }

        if (this.shouldCreateInterventionOnLaunch(selectedIntervention)) {
          // Assign crash to PCC Review and apply "new" status except for DriverINDEX Coach
          this.createNewIntervention(selectedIntervention).subscribe({
            next: (response: { submissionId: string }) => {
              this.currentFormSubmissionId = response.submissionId;
              this.getLaunchedDynamicFormStructure(response.submissionId);
            },
          });
        } else {
          this.getLaunchedDynamicFormStructure(formHistoryId, securePin);
        }

        if (!this.cService.isLargePcViewport) {
          this.selectedDriverPaneOpened = false;
        }
      } else {
        this.closeFormPane();
        this.cService.showToast(`:: ${this.cService.readyTranslations.messagesAndWarnings.requestedAssetNotAvailable}`);
      }

      console.log('%c Launched Intervention History', 'color: yellowgreen', {
        selectedHistory,
        launchedIntervention: this.launchedIntervention,
      });
    }, 100);
  }

  private getLaunchedDynamicFormStructure(formHistoryId: string, securePin = false) {
    this.getDynamicFormsStructAndDataForkJoined(
      this.launchedIntervention,
      this.selectedDriver.pin,
      formHistoryId,
      securePin
    ).subscribe({
      next: (response: Array<any>) => {
        this.selectedDynamicFormData = response[0].formData;

        // TODO: Replace with something more flexible.
        if (this.selectedDynamicFormData && this.selectedDynamicFormData['coachingReasonText']) {
          if (this.coachingReasonsDataMapping[this.selectedDynamicFormData['coachingReasonText']]) {
            this.selectedDynamicFormData['coachingReasonText'] =
              this.coachingReasonsDataMapping[this.selectedDynamicFormData['coachingReasonText']];
          }
        }

        if (response[1].form_structure && response[1].form_structure.metadata) {
          if (this.launchedIntervention && this.collapsibleInterventions.includes(this.launchedIntervention)) {
            response[1].form_structure.metadata.pageType =
              response[1].form_structure.pages.length > 1 ? 'accordion' : '';
          }

          this.currentFormStruct = response[1].form_structure;
        }

        this.showFormPane = true;

        const nominatedTab: string = this.cService.userProfile.companySettings.defaultCoachingTab;
        this.currentTabIndex = nominatedTab ? this.drilldownTabs[nominatedTab].id : TabsEnum.insights;
        !this.cService.isPcViewport ? (this.widenForm = true) : (this.widenForm = false);

        this.formVisibility.next(true);
      },

      error: (error) => {
        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.failedToProcessRequest);
        console.error(`Dynamic Form - ${this.launchedIntervention} - ERROR:`, error);
      },
    });
  }

  ViewSelectedIntervention(selectedHistory: any, selectedDriverPin: string) {
    setTimeout(() => {
      if (selectedHistory.interventionValue) {
        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.working);
        this.getRequestedDynamicFormStructureForViewing(selectedHistory, selectedDriverPin);
      } else {
        this.cService.showToast(`:: ${this.cService.readyTranslations.messagesAndWarnings.requestedAssetNotAvailable}`);
      }

      console.log('%c Intervention History in View', 'color: yellowgreen', selectedHistory);
    }, 100);
  }

  private getRequestedDynamicFormStructureForViewing(selectedHistory: any, driverPin: string) {
    this.getDynamicFormsStructAndDataForkJoined(
      selectedHistory.interventionValue,
      driverPin,
      selectedHistory.submissionId
    ).subscribe({
      next: (response: Array<any>) => {
        this.selectedViewOnlyDynamicFormData = response[0].formData;

        if (response[1].form_structure && response[1].form_structure.metadata) {
          this.currentViewOnlyFormStruct = response[1].form_structure;
        }

        this.viewOnlyFormVisibility.next(true);
      },

      error: (error) => {
        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.failedToProcessRequest);
        console.error(`Dynamic Form - ${this.launchedIntervention} - ERROR:`, error);
      },
    });
  }

  sendFileViaServer(fileIdentifier: number | string, pins: Array<unknown>, vehicleType?: string) {
    const data: any = {
      insight: fileIdentifier,
      employeeNumbers: pins,
    };

    if (vehicleType) {
      data.vehicle = vehicleType;
    }

    this.cService.commonPost(`roadrisk/profile_insights/mail`, data).subscribe({
      next: () => {
        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.requestProcessed);
      },
      error: () => {
        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.failedToProcessRequest);
      },
    });
  }

  // API
  private getActiveEventSummary(pin?: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`driverindex/active_event_summary/${pin}?securePin=${securePin}`);
  }

  private getHistoricEventSummary(pin?: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`driverindex/historic_event_summary/${pin}?securePin=${securePin}`);
  }

  getMentorRewards(pin?: string, securePin = false): Observable<any> {
    const mentorRewards$ = this.cService.commonGet(`driverindex/mentor_rewards/${pin}?securePin=${securePin}`);
    mentorRewards$.subscribe({
      next: (response) => this.setSelectedDriverMentorRewards(response),
    });
    return mentorRewards$;
  }

  setSelectedDriverMentorRewards(response: any) {
    this.selectedDriver.mentorRewards = {
      currentBalance: response.summary.currentPoints,
      lifetimeRewards: response.summary.lifetimePoints,
      numberOfEarnedBadges: response.summary.badgesEarned,
      earnedBadges: response.details,
    };
  }

  getVRMModulesSummary(pin?: string, securePin = false): Observable<any> {
    return this.cService.commonGet(
      `driverindex/vrm_module_activity/${pin}?securePin=${securePin}&language=${this.cService.selectedLanguage.code}`
    );
  }

  getVRMModulesHistoric(pin: string, securePin = false) {
    return this.cService.commonGet(
      `driverindex/vrm_module_historic/${pin}?securePin=${securePin}&language=${this.cService.selectedLanguage.code}`
    );
  }

  getNonVRMActivity(pin: string, securePin = false) {
    return this.cService.commonGet(`driverindex/non_vrm_activity/${pin}?securePin=${securePin}`);
  }

  private getMentorPlaylistSummary(pin: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`driverindex/mentor_playlist/summary/${pin}?securePin=${securePin}`);
  }

  private getMentorPlaylistModules(pin: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`driverindex/mentor_playlist/modules/${pin}?securePin=${securePin}`);
  }

  getCompanyPointsOverrideOptions(): Observable<any> {
    return this.cService.commonGet(`driverindex/override/company-points`);
  }

  getDriverRoadRiskVehicleTypes(pin?: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`roadrisk/${pin}/vehicle_types?securePin=${securePin}`);
  }

  getEventSummaryForkJoined(pin: string, securePin = false): Observable<any> {
    const requests: Array<Observable<any>> = [
      this.getActiveEventSummary(pin, securePin),
      this.getHistoricEventSummary(pin, securePin),
    ];

    return forkJoin(requests);
  }

  getMentorPlaylistForkJoined(pin: string, securePin = false): Observable<any> {
    const requests: Array<Observable<any>> = [
      this.getMentorPlaylistSummary(pin, securePin),
      this.getMentorPlaylistModules(pin, securePin),
    ];

    return forkJoin(requests);
  }

  private getDriverIndexSummary(pin: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`charts/driverindex/summary/${pin}?securePin=${securePin}`);
  }

  private getDriverIndexCoachSummary(pin: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`driverindex/coach_summary/${pin}?securePin=${securePin}`);
  }

  private getMentorCharts(pin: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`charts/driverindex/mentor/${pin}?securePin=${securePin}`);
  }

  private getDriverIndexSummaryFusionCharts(pin: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`charts/driverindex/summary/fusion_charts/${pin}?securePin=${securePin}`);
  }

  public getMentorFusionCharts(pin: string, securePin = false): Observable<any> {
    return this.cService.commonGet(`charts/driverindex/mentor/fusion_charts/${pin}?securePin=${securePin}`);
  }

  getCoachSummariesForkJoined(pin: string, securePin = false): Observable<any> {
    return forkJoin([
      this.getDriverIndexSummary(pin, securePin),
      this.getDriverIndexCoachSummary(pin, securePin),
      this.getMentorCharts(pin, securePin),
    ]);
  }

  getCoachSummariesWithFusionChartsForkJoined(pin: string, securePin = false): Observable<any> {
    return forkJoin([
      this.getDriverIndexSummaryFusionCharts(pin, securePin),
      this.getDriverIndexCoachSummary(pin, securePin),
      this.getMentorFusionCharts(pin, securePin),
    ]);
  }

  private getProfileInsightDetailsDriversMeta(insightId: number): Observable<any> {
    const vehicleType = this.rptService.requestedServerFilters.vehicle;
    const location = this.rptService.requestedServerFilters.location;
    const vehicleQuery = vehicleType ? `&vehicle=${vehicleType}` : '';
    const locationQuery = location ? `&location=${location}` : '';
    return this.cService.commonGet(
      `roadrisk/profile_insights/meta?insight=${insightId}${vehicleQuery}${locationQuery}`
    );
  }

  private getProfileInsightDetails(insightId: number): Observable<any> {
    const vehicleType = this.rptService.requestedServerFilters.vehicle;
    const location = this.rptService.requestedServerFilters.location;
    const vehicleQuery = vehicleType ? `&vehicle=${vehicleType}` : '';
    const locationQuery = location ? `&location=${location}` : '';
    return this.cService.commonGet(`roadrisk/profile_insights?insight=${insightId}${vehicleQuery}${locationQuery}`);
  }

  getProfileInsightDetailsForkJoined(insightId: number): Observable<any> {
    return forkJoin([this.getProfileInsightDetailsDriversMeta(insightId), this.getProfileInsightDetails(insightId)]);
  }

  // TODO: Remove once sure ECOLAB has settled into Dynamic Forms
  getDriverIndexCoachForm(pin: string, coachId: string): Observable<any> {
    return this.cService.commonGet(`driverindex/coach/${pin}/${coachId}`);
  }

  private getDynamicFormStructure(formName: string): Observable<any> {
    return this.cService.commonGet(`forms/structure?tag=${formName}`);
  }

  private getExistingDynamicFormsData(
    formName: string,
    employeePin: string,
    formHistoryId?: string,
    securePin = false
  ): Observable<any> {
    const path = `forms/data/${formName}/${employeePin}${formHistoryId ? '/' + formHistoryId : ''}`;
    return this.cService.commonGet(`${path}?securePin=${securePin}`);
  }

  getDynamicFormsStructAndDataForkJoined(
    formName: string,
    employeePin: string,
    formHistoryId?: string,
    securePin = false
  ): Observable<any> {
    return forkJoin([
      this.getExistingDynamicFormsData(formName, employeePin, formHistoryId, securePin),
      this.getDynamicFormStructure(formName),
    ]);
  }

  private createNewIntervention(interventionType: string): Observable<any> {
    const createData = {
      interventionType,
      securePin: this.selectedDriver.securePin ?? false,
      employeeNumber: this.selectedDriver.pin,
      crashId:
        this.selectedPccReviewCrash && this.selectedPccReviewCrash.crashId ? this.selectedPccReviewCrash.crashId : '',
    };

    return this.cService.commonPost(`coaching_interventions/create`, createData);
  }

  saveDynamicForm(formName: string, employeePin: string, formData: any, securePin = false): Observable<any> {
    return this.cService.commonPost(`forms/data/${formName}/${employeePin}?securePin=${securePin}`, formData);
  }

  getCoachingHistoryTabDataForkJoined(employeePin: any, securePin = false): Observable<any> {
    const historyPostData = {
      employeeNumber: employeePin,
    };

    return forkJoin([
      this.cService.commonGet(`coaching_interventions/list/${employeePin}?securePin=${securePin}`),
      this.cService.commonGet(`coaching_interventions/unassigned_crashes/${employeePin}?securePin=${securePin}`),

      this.cService.commonGet('coaching_interventions/history/meta'),
      this.cService.commonPost(`coaching_interventions/history?securePin=${securePin}`, historyPostData),
    ]);
  }

  getRoadRiskTabData(pin: string | number, vehicleType: string = 'car'): Observable<any> {
    return this.cService.commonGet(`driver_performance_record/roadrisk/${vehicleType}/${pin}`);
  }

  sendRoadRiskGuideDocument(questionId: string) {
    const postData = {
      question: questionId,
      employeeNumber: this.selectedDriver.pin,
    };

    this.cService.commonPost(`driver_performance_record/roadrisk/mail`, postData).subscribe({
      next: () => {
        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.requestProcessed);
      },
      error: () => {
        this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.failedToProcessRequest);
      },
    });
  }

  fetchOutstandingInterventionsPerDriver(pins: Array<any>, securePin = false): Observable<any> {
    return this.cService.commonPost(`coaching_interventions/outstanding?securePin=${securePin}`, {
      employeeNumbers: pins,
    });
  }

  public promptFormSubmissionConfirmation(event: DynamicFormsCompleteEvent): Observable<boolean> {
    this.cService.promptConfig = {
      title: event.formValid
        ? this.cService.readyTranslations.form.markSessionAsComplete
        : this.cService.readyTranslations.form.completeAllRequiredFields,

      body: event.formValid
        ? `<p>${this.cService.readyTranslations.form.coachingSessionWillNoMoreBeEditable}</p>${this.cService.readyTranslations.form.pressCompleteToConfirm}`
        : `${this.cService.readyTranslations.form.coachingCanOnlyBeMarkedCompleteIfRequiredFilled}`,

      actionButtonText: event.formValid ? this.cService.readyTranslations.reportData.complete : '',

      cancelButtonText: event.formValid
        ? this.cService.readyTranslations.form.cancel
        : this.cService.readyTranslations.generic.ok,
    };

    return this.dialog.open(PromptWithActionDialogComponent).afterClosed();
  }

  public promptDERFormSubmissionConfirmation(event: DynamicFormsCompleteEvent): Observable<boolean> {
    this.cService.promptConfig = {
      title: this.cService.readyTranslations.generic.vrmNotification,

      body: event.formValid
        ? `<p>${this.cService.readyTranslations.form.clickContinueToConfirmDerCompletion}</p>`
        : `${this.cService.readyTranslations.form.derCanOnlyBeMarkedCompleteIfRequiredFilled}`,

      actionButtonText: event.formValid
        ? this.cService.readyTranslations.form.continue
        : this.cService.readyTranslations.generic.ok,
      cancelButtonText: event.formValid ? this.cService.readyTranslations.form.cancel : '',
    };

    return this.dialog.open(PromptWithActionDialogComponent).afterClosed();
  }

  public getSelectedDriverIdentifier(shouldHidePin: boolean): string {
    return this.getDriverIdentifier(
      this.selectedDriver.pin,
      this.selectedDriver?.cdbId,
      this.selectedDriver?.realStaffNumber,
      shouldHidePin
    );
  }

  public getDriverIdentifier(pin: string, cdbId: string, realStaffNumber: string, shouldHidePin: boolean): string {
    if (
      this.cService.userProfile.company.toLowerCase().includes('amzl_jp_') ||
      this.cService.userProfile?.distributorName === 'amzl_jp'
    ) {
      return realStaffNumber;
    }

    if (this.cService.userProfile?.companyPermissions.permMisReportUseCdbId === this.cService.permLevels.TRUE) {
      return cdbId;
    }

    if (shouldHidePin) {
      return '';
    }

    return pin;
  }
}
